import { __rest } from 'tslib';
import React from 'react';
import { extractMarginProps } from '@10d/tend-ui-styling';
import merge from 'lodash/merge';
import { useTheme } from '@10d/tend-ui-theme';
import { INTERNAL_TendUILogger } from '@10d/tend-ui-utils';
import cn from 'classnames';
import { Hidden, SpinnerContainer, Spinner, Root } from './styled.js';

// TODO: Нужно передать дефолтное состояние `type="button"` в следующем мажоре
// иначе, все кнопки по дефолту ведут себя как `type="submit"` и триггерят формы
// если кнопка вложена в нее
function BaseButton(_a, ref) {
    var { padding = true, loading = false, disabled = false, danger = false, skeleton = false, fullWidth = false, variant = 'primary', size = 'medium', preset = 'default', before, after, children, onClick, className, UNSTABLE_styling } = _a, props = __rest(_a, ["padding", "loading", "disabled", "danger", "skeleton", "fullWidth", "variant", "size", "preset", "before", "after", "children", "onClick", "className", "UNSTABLE_styling"]);
    const theme = useTheme();
    const styling = React.useMemo(() => merge({
        buttonOnAccent: {
            primaryDefaultText: theme.colors.blue600,
            primaryHoverText: theme.colors.blue700,
            primaryPressedText: theme.colors.blue800,
            primaryFocusText: theme.colors.blue700,
            primaryDisabledText: theme.colors['white700-transparent'],
            ghostDefaultText: theme.colors.gray0,
            ghostHoverText: theme.colors.gray100,
            ghostPressedText: theme.colors.gray150,
            ghostFocusText: theme.colors.gray50,
            ghostDisabledText: theme.colors['white700-transparent'],
        },
    }, UNSTABLE_styling), [UNSTABLE_styling, theme.colors]);
    const hasChildren = !!children;
    const hasLeftIcon = !!before;
    const hasRightIcon = !!after;
    const hasBothIcons = hasLeftIcon && hasRightIcon;
    const hasOnlyLeftIcon = hasLeftIcon && !hasChildren;
    const hasOnlyRightIcon = hasRightIcon && !hasChildren;
    const isIconOnly = hasOnlyLeftIcon || hasOnlyRightIcon;
    const isPrimary = variant === 'primary';
    const isSecondary = variant === 'secondary';
    const isGhost = variant === 'ghost';
    const isLink = variant === 'link';
    const isLarge = size === 'large';
    const isMedium = size === 'medium';
    const isSmall = size === 'small';
    const isDefault = preset === 'default';
    const isDanger = preset === 'danger' || danger === true;
    const isAccent = preset === 'accent';
    const as = props.as || 'button';
    if (process.env.NODE_ENV === 'development') {
        if (variant === 'secondary' && isDanger) {
            INTERNAL_TendUILogger.warning([
                `<Button danger variant="${variant}" /> удален из дизайн системы.`,
                'и',
                '<Button variant="${variant}" preset="danger" />',
            ]);
        }
    }
    const shape = (() => {
        if (padding === false)
            return 'noPadding';
        if (isIconOnly)
            return 'iconOnly';
        if (hasBothIcons)
            return 'default';
        if (hasLeftIcon)
            return 'iconLeft';
        if (hasRightIcon)
            return 'iconRight';
        return 'default';
    })();
    const content = React.useMemo(() => {
        if (skeleton)
            return (React.createElement(Hidden, null,
                before,
                children,
                after));
        if (loading)
            return (React.createElement(SpinnerContainer, null,
                React.createElement(Spinner, { size: 'xs' }),
                React.createElement(Hidden, null,
                    before,
                    children,
                    after)));
        return (React.createElement(React.Fragment, null,
            before,
            children,
            after));
    }, [skeleton, before, children, after, loading]);
    const handleClick = React.useCallback((e) => {
        if (loading || disabled) {
            e.preventDefault();
            return;
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    }, [disabled, loading, onClick]);
    const _b = extractMarginProps(props), { rest } = _b, margins = __rest(_b, ["rest"]);
    return (React.createElement(Root, Object.assign({ "data-testid": 'tend-ui-button' }, rest, margins, { ref: ref, theme: theme, "$skeleton": skeleton, "$danger": danger, "$disabled": disabled, "$variant": variant, "$size": size, "$preset": preset, "$tag": as, "$fullWidth": fullWidth, "$shape": shape, "$styling": styling, as: as, disabled: disabled, onClick: handleClick, className: cn(['tend-ui-button', className], {
            ['tend-ui-button-disabled']: disabled,
            ['tend-ui-button-loading']: loading,
            ['tend-ui-button-skeleton']: skeleton,
            ['tend-ui-button-variant-primary']: isPrimary,
            ['tend-ui-button-variant-secondary']: isSecondary,
            ['tend-ui-button-variant-ghost']: isGhost,
            ['tend-ui-button-variant-link']: isLink,
            ['tend-ui-button-preset-default']: isDefault,
            ['tend-ui-button-preset-danger']: isDanger,
            ['tend-ui-button-preset-accent']: isAccent,
            ['tend-ui-button-size-large']: isLarge,
            ['tend-ui-button-size-medium']: isMedium,
            ['tend-ui-button-size-small']: isSmall,
        }) }), content));
}
const Button = React.forwardRef(BaseButton);
Button.displayName = 'Button';

export { Button };
//# sourceMappingURL=Button.js.map
